import React, { useState, useEffect } from "react";
import { useLocation, Link, useSearchParams, useNavigate } from "react-router-dom";
import SignupForm from "../components/signupForm";
import { Button, Box, Typography, Paper, CircularProgress, Alert, TextField } from "@mui/material";
import Breadcrumbs from '../components/Breadcrumbs';
import OTPInput from "../components/OTPInput";
import { useUser } from "../context/UserContext";
import { useMemberAuth } from "../context/MemberAuthContext";
import { useChatBot } from "../context/ChatBotContext";
import { useSettings } from "../context/SettingsContext";
import { apiFetch } from "../lib/supabase";
import UserHeader from "../components/UserHeader";
import MaintenanceBanner from "../components/MaintenanceBanner";
import "date-fns";
import "./registration.css";

const parseFamilyMembers = (familyMembersStr) => {
  if (!familyMembersStr || familyMembersStr.trim() === "") return [];
  const entries = familyMembersStr.split(/[,\n]/).map(s => s.trim()).filter(s => s.length > 0);
  return entries.map((entry) => {
    const dobMatch = entry.match(/\((\d{4}-\d{2}-\d{2})/);
    const dob = dobMatch ? dobMatch[1] : "";
    const name = entry.replace(/\s*\([^)]*\)/g, '').replace(/Swimming permission|Accompanied by adult/gi, '').trim();
    const statusMatch = entry.match(/(Swimming permission|Accompanied by adult)/i);
    const status = statusMatch ? statusMatch[1] : "";
    return { name, dob, status };
  });
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const prefillFromMemberData = (memberData) => ({
  firstName: memberData.first_name || "",
  lastName: memberData.name || "",
  contactEmail: memberData.email || "",
  address: memberData.address || "",
  phoneNumber: memberData.phone_number || "",
  rotaPublish: memberData.incl_phone_in_rota === "Yes",
  inclSmsReminders: memberData.incl_sms_reminders !== "No",
  dutyType: memberData.duty_type || "Administrator",
  familyMembers: parseFamilyMembers(memberData.family_members),
  comments: memberData.comments || "",
});

const prefillAllFieldsFromMemberData = (memberData) => ({
  firstName: memberData.first_name || "",
  lastName: memberData.name || "",
  contactEmail: memberData.email || "",
  address: memberData.address || "",
  phoneNumber: memberData.phone_number || "",
  rotaPublish: memberData.incl_phone_in_rota === "Yes",
  inclSmsReminders: memberData.incl_sms_reminders !== "No",
  dutyType: memberData.duty_type || "Administrator",
  familyMembers: parseFamilyMembers(memberData.family_members),
  comments: memberData.comments || "",
  dutyMon: memberData.monday_duty === "Yes",
  dutyTue: memberData.tuesday_duty === "Yes",
  dutyWed: memberData.wednesday_duty === "Yes",
  dutyFri: memberData.friday_duty === "Yes",
  dutySat: memberData.saturday_duty === "Yes",
  dutySun: memberData.sunday_duty === "Yes",
  dutyHol1: memberData.school_hol_tuesday_duty === "Yes",
  dutyHol2: memberData.school_hol_thursday_duty === "Yes",
  moreDuties: memberData.more_duties || "",
  hadPriorTraining: memberData.had_prior_training || "yes",
  unavailableDates: memberData.unavailable_dates || [],
  holidaysPeriods: memberData.holiday_periods ? memberData.holiday_periods.split(',').map(s => s.trim()).filter(s => s) : [],
  theorySession: memberData.second_theory_session || "",
  poolSession: memberData.second_pool_session || "",
});

export default function Signup() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRenewal = location.state?.isRenewal || false;
  const isUpdateOnly = location.state?.isUpdateOnly || false;
  const isAdminAdd = searchParams.get('adminAdd') === 'true';
  const memberData = location.state?.memberData || null;
  const scrollToSection = location.state?.scrollToSection || null;
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [retrieveStep, setRetrieveStep] = useState("email");
  const [retrieveLoading, setRetrieveLoading] = useState(false);
  const [retrieveMessage, setRetrieveMessage] = useState("");
  const [retrieveError, setRetrieveError] = useState("");
  const [tokenRenewal, setTokenRenewal] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [tokenMemberId, setTokenMemberId] = useState(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [registeredMemberData, setRegisteredMemberData] = useState(null);
  const { updateMultipleFields } = useUser();
  const { requestCode, verifyCode, isDevEnvironment, isAuthenticated, member } = useMemberAuth();
  const { setPageContent, clearPageContent } = useChatBot();
  const { getPricingConfig, getTheorySessions, getPoolSessions, getFirstOfficerTrainingDay, getFirstOfficerPoolSessions } = useSettings();
  
  const pricing = getPricingConfig();
  const theorySessions = getTheorySessions();
  const poolSessions = getPoolSessions();
  const firstOfficerTrainingDay = getFirstOfficerTrainingDay();
  const firstOfficerPoolSessions = getFirstOfficerPoolSessions();

  useEffect(() => {
    if (scrollToSection) {
      const timer = setTimeout(() => {
        const el = document.getElementById(scrollToSection);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [scrollToSection]);

  useEffect(() => {
    const formatDate = (dateStr) => {
      if (!dateStr) return 'TBC';
      return new Date(dateStr).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
    };
    
    const cutoffDateStr = pricing.CUTOFF_DATE ? new Date(pricing.CUTOFF_DATE).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : 'early April';
    
    const theorySessionsList = theorySessions.length > 0 
      ? theorySessions.map(s => formatDate(s)).join(', ')
      : 'Dates to be confirmed';
    
    const poolSessionsList = poolSessions.length > 0
      ? poolSessions.map(s => formatDate(s)).join(', ')
      : 'Dates to be confirmed';
    
    const firstOfficerTrainingStr = firstOfficerTrainingDay ? formatDate(firstOfficerTrainingDay) : 'Date to be confirmed';
    
    const firstOfficerPoolStr = firstOfficerPoolSessions.length > 0
      ? firstOfficerPoolSessions.map(s => formatDate(s)).join(', ')
      : 'Dates to be confirmed';
    
    const content = `CCSC Registration Page - Membership Registration Form

Registration Form Fields:
- First Name and Last Name
- Email Address (contact email for the household)
- Address
- Mobile Number
- Family Members (for family memberships - add names and dates of birth)

Duty Selection:
- Administrator: No special qualifications needed, must be over 18, check admissions and administer first aid
- 1st Safety Officer: Must hold RLSS qualification or CCSC Safety Officer qualification, patrols poolside, ${pricing.FIRST_OFFICER_DISCOUNT}% membership discount
- 2nd Safety Officer: Assists 1st Safety Officer, must be good swimmer over 18, validated at training sessions

Duty Days:
- Regular days: Monday, Tuesday, Wednesday, Friday (evenings)
- Weekend days: Saturday, Sunday (afternoons)
- School holiday sessions: Tuesday and Thursday (daytime)

Training Sessions for 2nd Safety Officers:
- Theory sessions: ${theorySessionsList}
- Pool sessions: ${poolSessionsList}

Training for 1st Safety Officers (new officers only):
- Training day: ${firstOfficerTrainingStr}
- Pool sessions: ${firstOfficerPoolStr}

Membership Pricing:
- Individual membership: £${pricing.INDIVIDUAL_EARLY} (before ${cutoffDateStr}) / £${pricing.INDIVIDUAL_LATE} (after)
- Family membership: £${pricing.FAMILY_EARLY} (before ${cutoffDateStr}) / £${pricing.FAMILY_LATE} (after)
- 1st Safety Officers receive ${pricing.FIRST_OFFICER_DISCOUNT}% discount

Returning Members:
- Can retrieve previous year's details by entering email
- Details are pre-filled for easy renewal`;
    
    setPageContent(content);
    return () => clearPageContent();
  }, [setPageContent, clearPageContent, pricing, theorySessions, poolSessions, firstOfficerTrainingDay, firstOfficerPoolSessions]);
  
  useEffect(() => {
    if (!isRenewal && !isUpdateOnly && isAuthenticated && member) {
      const fetchMemberAndRedirect = async () => {
        try {
          const { member: m } = await apiFetch(
            `/api/member/by-email?email=${encodeURIComponent(member.email)}`
          );
          if (m) {
            navigate('/register', {
              state: { isRenewal: true, memberData: m },
              replace: true
            });
          }
        } catch (err) {
          console.error("Error fetching member data for renewal:", err);
        }
      };
      fetchMemberAndRedirect();
    }
  }, [isRenewal, isUpdateOnly, isAuthenticated, member, navigate]);

  useEffect(() => {
    if ((isRenewal || isUpdateOnly) && memberData) {
      if (isUpdateOnly) {
        updateMultipleFields(prefillAllFieldsFromMemberData(memberData));
      } else {
        updateMultipleFields(prefillFromMemberData(memberData));
      }
    }
  }, [isRenewal, isUpdateOnly, memberData]);

  useEffect(() => {
    const renewalToken = searchParams.get('renewalToken');

    if (renewalToken) {
      const validateRenewalToken = async () => {
        setTokenLoading(true);
        setTokenError("");
        try {
          const response = await fetch(`/api/renewal/validate?token=${encodeURIComponent(renewalToken)}`);
          const result = await response.json();

          if (response.ok && result.success) {
            const m = result.member;

            if (result.alreadyRegistered) {
              setAlreadyRegistered(true);
              setRegisteredMemberData(m);
              setTokenMemberId(m.id);
            } else {
              const memberDataFromToken = {
                first_name: m.firstName,
                name: m.lastName,
                email: m.email,
                address: m.address,
                phone_number: m.phoneNumber,
                incl_phone_in_rota: m.rotaPublish ? "Yes" : "No",
                incl_sms_reminders: m.inclSmsReminders ? "Yes" : "No",
                duty_type: m.dutyType,
                family_members: m.familyMembers,
                friday_duty: m.fridayDuty,
                saturday_duty: m.saturdayDuty,
                sunday_duty: m.sundayDuty,
                monday_duty: m.mondayDuty,
                tuesday_duty: m.tuesdayDuty,
                wednesday_duty: m.wednesdayDuty,
                school_hol_thursday_duty: m.schoolHolThursdayDuty,
                school_hol_tuesday_duty: m.schoolHolTuesdayDuty,
                unavailable_dates: m.unavailableDates,
                more_duties: m.moreDuties,
                had_prior_training: m.hadPriorTraining,
                holiday_periods: m.holidayPeriods,
                second_theory_session: m.secondTheorySession,
                second_pool_session: m.secondPoolSession,
                comments: m.comments,
                id: m.id,
              };
              updateMultipleFields(prefillAllFieldsFromMemberData(memberDataFromToken));
              setEmail(m.email || "");
              setTokenMemberId(m.id);
              setTokenRenewal(true);
              setRetrieveStep("done");
              setRetrieveMessage("Welcome back! Your details have been loaded. Please review and update them below.");
            }
          } else {
            setTokenError(result.error || "This renewal link is invalid or has expired.");
          }
        } catch (err) {
          console.error("Error validating renewal token:", err);
          setTokenError("Unable to validate your renewal link. Please try again or contact the club.");
        } finally {
          setTokenLoading(false);
        }
      };
      validateRenewalToken();
    }
  }, [searchParams]);

  const fetchAndPrefillMember = async (memberEmail) => {
    try {
      const { member: m } = await apiFetch(
        `/api/member/by-email?email=${encodeURIComponent(memberEmail.trim())}`
      );

      if (m) {
        updateMultipleFields(prefillFromMemberData(m));
        setRetrieveMessage("Your details have been retrieved and filled in below.");
        setRetrieveStep("done");
      } else {
        setRetrieveMessage("No existing membership found. Please fill in your details below.");
        setRetrieveStep("done");
      }
    } catch (err) {
      console.error("Error fetching member:", err);
      setRetrieveError("Unable to retrieve details. Please try again.");
    }
  };
  
  const handleRequestCode = async () => {
    if (!email.trim()) {
      setRetrieveError("Please enter your email address");
      return;
    }
    
    setRetrieveLoading(true);
    setRetrieveError("");
    setRetrieveMessage("");
    
    await requestCode(email);
    if (isDevEnvironment) {
      setRetrieveMessage("Dev mode: Enter code 123456 to continue");
    } else {
      setRetrieveMessage("If this email is registered, a verification code has been sent");
    }
    setRetrieveStep("code");
    setRetrieveLoading(false);
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      setRetrieveError("Please enter the verification code");
      return;
    }
    
    setRetrieveLoading(true);
    setRetrieveError("");
    
    const result = await verifyCode(email, code);
    
    if (result.success) {
      await fetchAndPrefillMember(email);
    } else {
      setRetrieveError(result.error || "Invalid verification code");
    }
    
    setRetrieveLoading(false);
  };

  const handleBackToEmail = () => {
    setRetrieveStep("email");
    setCode("");
    setRetrieveError("");
    setRetrieveMessage("");
  };
  
  return (
    <div className="register-page">
      <UserHeader />
      <MaintenanceBanner message="The site is undergoing maintenance. New membership sign-ups are temporarily unavailable — please check back shortly." />
      <div className="register-hero">
        <div className="register-hero-content">
          <h1 className="register-hero-title">
            <span>{isAdminAdd ? 'Add New' : (isUpdateOnly ? 'Update your' : (alreadyRegistered ? 'Your' : ((isRenewal || tokenRenewal) ? 'Renew your' : 'Become a')))}</span>
            <br />
            <span>{isAdminAdd ? 'Member' : (isUpdateOnly ? 'Details' : (alreadyRegistered ? 'Registration' : ((isRenewal || tokenRenewal) ? 'Membership' : 'Member')))}</span>
          </h1>
          <p className="register-hero-subtitle">{isAdminAdd ? 'Admin: Add a member manually without payment' : (isUpdateOnly ? 'Update your contact information and preferences' : (alreadyRegistered ? `You're already registered for the ${currentYear} season` : ((isRenewal || tokenRenewal) ? 'Continue your membership for the new season' : 'Join our swimming community')))}</p>
        </div>
      </div>
      
      <Box sx={{ maxWidth: 800, margin: '0 auto', padding: '16px 24px 0' }}>
        <Breadcrumbs customLabel={isAdminAdd ? 'Add Member (Admin)' : (isUpdateOnly ? 'Update Details' : (alreadyRegistered ? 'Your Registration' : ((isRenewal || tokenRenewal) ? 'Renew Membership' : 'Registration')))} parents={isUpdateOnly ? [{ path: '/members', label: 'Members Area' }] : undefined} />
      </Box>
      
      {tokenLoading && (
        <Box sx={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px', textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">Loading your membership details...</Typography>
        </Box>
      )}

      {tokenError && (
        <Box sx={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
          <Alert severity="error" sx={{ mb: 2 }}>{tokenError}</Alert>
          <Typography variant="body2" sx={{ color: '#666' }}>
            You can still register as a new member or use your email to retrieve your details below.
          </Typography>
        </Box>
      )}

      {alreadyRegistered && registeredMemberData && (
        <Box sx={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
          <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>
            You have already registered for the {currentYear} season. Here are your current details on file.
          </Alert>

          <Paper
            elevation={0}
            sx={{
              padding: '32px',
              borderRadius: '16px',
              border: '2px solid #121212',
              backgroundColor: '#fff',
              mb: 3
            }}
          >
            <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54', mb: 3 }}>
              Personal Details
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px 32px', mb: 3 }}>
              <Box>
                <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>First Name</Typography>
                <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>{registeredMemberData.firstName || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>Last Name</Typography>
                <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>{registeredMemberData.lastName || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>Email</Typography>
                <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>{registeredMemberData.email || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>Phone</Typography>
                <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>{registeredMemberData.phoneNumber || '-'}</Typography>
              </Box>
              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>Address</Typography>
                <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>{registeredMemberData.address || '-'}</Typography>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #eee', pt: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54', mb: 2 }}>
                Duty Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px 32px' }}>
                <Box>
                  <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>Duty Type</Typography>
                  <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>{registeredMemberData.dutyType || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>Phone on Rota</Typography>
                  <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>{registeredMemberData.rotaPublish ? 'Yes' : 'No'}</Typography>
                </Box>
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>Duty Days</Typography>
                  <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>
                    {[
                      registeredMemberData.mondayDuty === 'Yes' && 'Monday',
                      registeredMemberData.tuesdayDuty === 'Yes' && 'Tuesday',
                      registeredMemberData.wednesdayDuty === 'Yes' && 'Wednesday',
                      registeredMemberData.fridayDuty === 'Yes' && 'Friday',
                      registeredMemberData.saturdayDuty === 'Yes' && 'Saturday',
                      registeredMemberData.sundayDuty === 'Yes' && 'Sunday',
                    ].filter(Boolean).join(', ') || '-'}
                  </Typography>
                </Box>
                {(registeredMemberData.schoolHolTuesdayDuty === 'Yes' || registeredMemberData.schoolHolThursdayDuty === 'Yes') && (
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                    <Typography variant="caption" sx={{ fontFamily: "'General Sans', sans-serif", color: '#999', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>School Holiday Duties</Typography>
                    <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>
                      {[
                        registeredMemberData.schoolHolTuesdayDuty === 'Yes' && 'Tuesday',
                        registeredMemberData.schoolHolThursdayDuty === 'Yes' && 'Thursday',
                      ].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {registeredMemberData.familyMembers && registeredMemberData.familyMembers.trim() !== '' && (
              <Box sx={{ borderTop: '1px solid #eee', pt: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54', mb: 2 }}>
                  Family Members
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500, whiteSpace: 'pre-line' }}>
                  {registeredMemberData.familyMembers}
                </Typography>
              </Box>
            )}

            {registeredMemberData.comments && registeredMemberData.comments.trim() !== '' && (
              <Box sx={{ borderTop: '1px solid #eee', pt: 3 }}>
                <Typography variant="h6" sx={{ fontFamily: "'Chillax', sans-serif", fontWeight: 600, color: '#0d2c54', mb: 2 }}>
                  Comments
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: "'General Sans', sans-serif", fontWeight: 500 }}>
                  {registeredMemberData.comments}
                </Typography>
              </Box>
            )}
          </Paper>

          <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/members')}
              sx={{
                backgroundColor: '#dbf14e',
                color: '#121212',
                border: '2px solid #121212',
                borderRadius: '100px',
                fontFamily: "'General Sans', sans-serif",
                fontWeight: 600,
                textTransform: 'none',
                padding: '12px 32px',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#c5d946',
                }
              }}
            >
              Edit My Details
            </Button>
            <Button
              variant="outlined"
              component={Link}
              to="/"
              sx={{
                color: '#121212',
                border: '2px solid #121212',
                borderRadius: '100px',
                fontFamily: "'General Sans', sans-serif",
                fontWeight: 600,
                textTransform: 'none',
                padding: '12px 32px',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Box>
      )}

      {!isRenewal && !isUpdateOnly && !isAdminAdd && !tokenRenewal && !tokenLoading && !alreadyRegistered && (
        <Box sx={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              padding: '24px 32px', 
              borderRadius: '16px',
              border: '1px solid #e0e0e0',
              backgroundColor: '#fafafa'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: "'Chillax', sans-serif",
                fontWeight: 600,
                color: '#0d2c54',
                marginBottom: '8px'
              }}
            >
              Returning Member?
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: "'General Sans', sans-serif",
                color: '#666',
                marginBottom: '16px'
              }}
            >
              {retrieveStep === "email" && "Enter your email address to retrieve your existing membership details."}
              {retrieveStep === "code" && "Enter the verification code sent to your email. Not received the code? Check your spam folder before contacting contact@comberton-pool.org.uk"}
              {retrieveStep === "done" && "Your details have been loaded."}
            </Typography>
            {retrieveError && (
              <Alert severity="error" sx={{ mb: 2 }}>{retrieveError}</Alert>
            )}
            {retrieveMessage && (
              <Alert severity={retrieveStep === "done" ? "success" : "info"} sx={{ mb: 2 }}>{retrieveMessage}</Alert>
            )}
            
            {retrieveStep === "email" && (
              <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={retrieveLoading}
                  sx={{ 
                    flex: 1, 
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#fff'
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleRequestCode}
                  disabled={retrieveLoading || !isValidEmail(email)}
                  sx={{
                    backgroundColor: '#dbf14e',
                    color: '#121212',
                    fontFamily: "'General Sans', sans-serif",
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '100px',
                    padding: '8px 24px',
                    border: '2px solid #121212',
                    '&:hover': {
                      backgroundColor: '#c9df3e'
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      borderColor: '#999'
                    }
                  }}
                >
                  {retrieveLoading ? <CircularProgress size={20} /> : 'Send Code'}
                </Button>
              </Box>
            )}

            {retrieveStep === "code" && (
              <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <OTPInput
                  value={code}
                  onChange={setCode}
                  disabled={retrieveLoading}
                />
                <Button
                  variant="contained"
                  onClick={handleVerifyCode}
                  disabled={retrieveLoading}
                  sx={{
                    backgroundColor: '#dbf14e',
                    color: '#121212',
                    fontFamily: "'General Sans', sans-serif",
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '100px',
                    padding: '8px 24px',
                    border: '2px solid #121212',
                    '&:hover': {
                      backgroundColor: '#c9df3e'
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      borderColor: '#999'
                    }
                  }}
                >
                  {retrieveLoading ? <CircularProgress size={20} /> : 'Verify'}
                </Button>
                <Button
                  variant="text"
                  onClick={async () => {
                    setRetrieveLoading(true);
                    await requestCode(email);
                    setRetrieveMessage(isDevEnvironment ? 'Dev mode: Enter code 123456 to continue' : 'Code resent! Check your inbox');
                    setRetrieveLoading(false);
                  }}
                  disabled={retrieveLoading}
                  sx={{
                    color: '#0d2c54',
                    fontFamily: "'General Sans', sans-serif",
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Resend
                </Button>
                <Button
                  variant="text"
                  onClick={handleBackToEmail}
                  disabled={retrieveLoading}
                  sx={{
                    color: '#666',
                    fontFamily: "'General Sans', sans-serif",
                    textTransform: 'none',
                  }}
                >
                  Back
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      )}
      
      {tokenRenewal && (
        <Box sx={{ maxWidth: 800, margin: '0 auto', padding: '16px 24px 0' }}>
          <Alert severity="success" sx={{ mb: 0 }}>
            Welcome back! Your details have been loaded from your renewal invitation. Please review and update them below.
          </Alert>
        </Box>
      )}

      {!tokenLoading && !alreadyRegistered && (
        <div className="register-form-container">
          <SignupForm isUpdateOnly={isUpdateOnly} memberId={memberData?.id || tokenMemberId} isAdminAdd={isAdminAdd} />
        </div>
      )}
      <div className="register-footer">
        <span className="register-footer-text">
          © Comberton Community Swimming Club, {currentYear}.
        </span>
      </div>
    </div>
  );
}